import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import { Button } from './Button'
import { RecipientLanguagePicker } from './RecipientLanguagePicker'
import { formatEmail, isValidEmail } from '../utils/email'
import { normaliseLanguage, type ReportLanguage } from '../utils/downloadParticipantReport'

// ── Types ─────────────────────────────────────────────────────────────────────

/** One row in the invite modal form. */
interface InviteRow {
  /** Locally unique key for React rendering. */
  key: number
  email: string
  customMessage: string
  language: ReportLanguage
}

interface Props {
  onConfirm: (
    entries: { email: string; customMessage?: string; language: ReportLanguage }[]
  ) => void
  onCancel: () => void
  error?: string | null
  /** When true, disables interactions and shows a spinner on the confirm button. */
  isSending?: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Splits a single invitation's email field into individual addresses (comma-separated). */
function parseEmailList(value: string): string[] {
  return value
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e.length > 0)
}

/** Valid addresses in this row, normalized — casing/full-width variants collapse to one form
 *  so the same person typed twice (e.g. copy-pasted with different capitalization) is only
 *  invited once, and so it matches exactly what the backend will store (see #175). */
function validEmailsInRow(row: InviteRow): string[] {
  return parseEmailList(row.email)
    .filter(isValidEmail)
    .map((e) => formatEmail(e))
}

function invalidEmailsInRow(row: InviteRow): string[] {
  return parseEmailList(row.email).filter((e) => !isValidEmail(e))
}

let rowCounter = 0
function nextKey() {
  return ++rowCounter
}

function emptyRow(language: ReportLanguage): InviteRow {
  return { key: nextKey(), email: '', customMessage: '', language }
}

// ── Icons ─────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const SpinnerIcon = () => (
  <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
    />
  </svg>
)

// ── Component ─────────────────────────────────────────────────────────────────

const inputCls =
  'border-pax-purple/30 text-pax-black placeholder:text-pax-gray/50 focus:border-pax-blue w-full rounded-xl border bg-white px-3 py-2 text-sm transition-colors outline-none'

/** Modal for sending one or more invitations by email.
 *  Each invitation has its own optional custom message and can target multiple recipients
 *  at once by entering several comma-separated addresses in the same field.
 *  Blank/invalid entries are ignored on submit. */
export function InviteModal({ onConfirm, onCancel, error, isSending = false }: Props) {
  const { t, i18n } = useTranslation()
  const defaultLanguage = normaliseLanguage(i18n.language)
  const [rows, setRows] = useState<InviteRow[]>(() => [emptyRow(defaultLanguage)])

  function updateRow(key: number, patch: Partial<Omit<InviteRow, 'key'>>) {
    if (isSending) return
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  function addRow() {
    if (isSending) return
    setRows((prev) => [...prev, emptyRow(defaultLanguage)])
  }

  function removeRow(key: number) {
    if (isSending) return
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev))
  }

  // Each invitation (row) can target multiple recipients at once — every address in the
  // comma-separated list shares that invitation's custom message. Emails are normalized
  // (see validEmailsInRow), so de-duping by exact match here also catches casing/full-width
  // repeats across rows/lists — no point sending the same person two invites in one batch.
  const seenEmails = new Set<string>()
  const entries = rows.flatMap((r) =>
    validEmailsInRow(r)
      .filter((email) => {
        if (seenEmails.has(email)) return false
        seenEmails.add(email)
        return true
      })
      .map((email) => ({
        email,
        customMessage: r.customMessage.trim() || undefined,
        language: r.language,
      }))
  )
  const canConfirm = entries.length > 0 && !isSending

  function handleConfirm() {
    if (!canConfirm) return
    onConfirm(entries)
  }

  return (
    <Modal labelledBy="invite-modal-title" onBackdropClick={isSending ? undefined : onCancel}>
      <div className="border-pax-purple/50 flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border bg-white shadow-2xl ring-1 ring-black/5">
        {/* Header */}
        <div className="border-pax-purple/20 flex items-center justify-between border-b px-6 py-4">
          <h2 id="invite-modal-title" className="text-pax-black text-lg font-bold">
            {t('invite_modal_title')}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSending}
            className="text-pax-gray hover:text-pax-black cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t('btn_close')}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-pax-gray mb-5 text-sm">{t('invite_modal_subtitle')}</p>

          {error && (
            <p
              role="alert"
              className="border-pax-red/30 bg-pax-red/5 text-pax-red mb-5 rounded-xl border px-4 py-3 text-sm"
            >
              {error}
            </p>
          )}

          <div className="flex flex-col gap-4">
            {rows.map((row, idx) => (
              <div
                key={row.key}
                className="border-pax-purple/20 rounded-xl border bg-white p-4 shadow-sm"
              >
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-pax-gray text-xs font-semibold tracking-wider uppercase">
                    {t('invite_modal_invitation')} {idx + 1}
                  </span>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(row.key)}
                      disabled={isSending}
                      className="text-pax-red ml-auto rounded-lg p-1 transition-opacity hover:opacity-70 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Remove invitation ${idx + 1}`}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  {/* Email(s) */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`inv-email-${row.key}`}
                      className="text-pax-black text-xs font-medium"
                    >
                      {t('invite_modal_email')}
                      <span className="text-pax-red ml-0.5">*</span>
                    </label>
                    <input
                      id={`inv-email-${row.key}`}
                      type="text"
                      value={row.email}
                      onChange={(e) => updateRow(row.key, { email: e.target.value })}
                      placeholder="participant@example.com, another@example.com"
                      disabled={isSending}
                      className={[
                        inputCls,
                        row.email && invalidEmailsInRow(row).length > 0
                          ? 'border-pax-red focus:border-pax-red'
                          : '',
                        isSending ? 'opacity-60' : '',
                      ].join(' ')}
                    />
                    <p className="text-pax-gray text-xs">{t('invite_modal_email_hint')}</p>
                    {row.email && invalidEmailsInRow(row).length > 0 && (
                      <p className="text-pax-red text-xs">{t('invite_modal_email_invalid')}</p>
                    )}
                  </div>

                  {/* Custom message */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`inv-msg-${row.key}`}
                      className="text-pax-black text-xs font-medium"
                    >
                      {t('invite_modal_message')}
                      <span className="text-pax-gray ml-1 text-xs font-normal">
                        ({t('invite_modal_message_optional')})
                      </span>
                    </label>
                    <textarea
                      id={`inv-msg-${row.key}`}
                      value={row.customMessage}
                      onChange={(e) => updateRow(row.key, { customMessage: e.target.value })}
                      placeholder={t('invite_modal_message_placeholder')}
                      disabled={isSending}
                      className={`${inputCls} min-h-[72px] resize-y${isSending ? 'opacity-60' : ''}`}
                    />
                    <p className="text-pax-gray text-xs">{t('invite_modal_message_hint')}</p>
                  </div>

                  <RecipientLanguagePicker
                    id={`inv-lang-${row.key}`}
                    value={row.language}
                    onChange={(language) => updateRow(row.key, { language })}
                    hint={t('invite_modal_language_hint')}
                    disabled={isSending}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Add another invitation */}
          <button
            type="button"
            onClick={addRow}
            disabled={isSending}
            className="border-pax-purple/30 hover:border-pax-blue text-pax-gray hover:text-pax-blue mt-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm font-medium transition-colors focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PlusIcon />
            {t('invite_modal_add_invitation')}
          </button>
        </div>

        {/* Footer — stacks on narrow screens: the ready-count text plus two full-width
            buttons don't fit on one row on a phone, which used to force the whole modal
            wider than the viewport instead of wrapping. */}
        <div className="border-pax-purple/20 flex flex-col gap-3 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-pax-gray text-xs" role={isSending ? 'status' : undefined}>
            {isSending
              ? t('invite_modal_sending')
              : entries.length > 0 && t('invite_modal_ready_count', { count: entries.length })}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={isSending}
              className="w-full sm:w-auto"
            >
              {t('btn_cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!canConfirm}
              leftIcon={isSending ? <SpinnerIcon /> : undefined}
              className="w-full sm:w-auto"
            >
              {isSending ? t('invite_modal_sending') : t('invite_modal_confirm')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
